import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import NavBar from '../components/NavBar';
import { gethinhAPI, contactAPI, formatApiError } from '../lib/api';
import { Download, Shield, Sparkles, CheckCircle2, CreditCard, BadgePercent, Zap, X, Send } from 'lucide-react';

const POPULAR_ORDER = ['shutterstock', 'adobestock', 'envato', 'freepik', 'istock', 'gettyimages', 'vecteezy', 'storyblocks', 'motionarray', 'flaticon'];

const PROVIDER_LOGOS = {
  shutterstock: { name: 'Shutterstock', color: 'from-rose-500/30 to-rose-700/15', text: 'text-rose-300', popular: true },
  adobestock: { name: 'Adobe Stock', color: 'from-red-500/30 to-red-700/15', text: 'text-red-300', popular: true },
  envato: { name: 'Envato', color: 'from-emerald-500/30 to-emerald-700/15', text: 'text-emerald-300', popular: true },
  freepik: { name: 'Freepik', color: 'from-blue-500/30 to-blue-700/15', text: 'text-blue-300', popular: true },
  istock: { name: 'iStock', color: 'from-amber-500/20 to-amber-700/10', text: 'text-amber-300' },
  vecteezy: { name: 'Vecteezy', color: 'from-teal-500/20 to-teal-700/10', text: 'text-teal-300' },
  flaticon: { name: 'Flaticon', color: 'from-sky-500/20 to-sky-700/10', text: 'text-sky-300' },
  storyblocks: { name: 'Storyblocks', color: 'from-orange-500/20 to-orange-700/10', text: 'text-orange-300' },
  motionarray: { name: 'Motion Array', color: 'from-pink-500/20 to-pink-700/10', text: 'text-pink-300' },
  pngtree: { name: 'PNGTree', color: 'from-purple-500/20 to-purple-700/10', text: 'text-purple-300' },
  gettyimages: { name: 'Getty Images', color: 'from-zinc-500/20 to-zinc-700/10', text: 'text-zinc-300' },
  depositphotos: { name: 'Depositphotos', color: 'from-cyan-500/20 to-cyan-700/10', text: 'text-cyan-300' },
};

const SAMPLE_IMAGES = [
  'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=600&q=70&auto=format',
  'https://images.unsplash.com/photo-1557672172-298e090bd0f1?w=600&q=70&auto=format',
  'https://images.unsplash.com/photo-1542831371-29b0f74f9713?w=600&q=70&auto=format',
  'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=600&q=70&auto=format',
  'https://images.unsplash.com/photo-1620207418302-439b387441b0?w=600&q=70&auto=format',
  'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=600&q=70&auto=format',
];

export default function Landing() {
  const [pricing, setPricing] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    gethinhAPI.getPricing().then(r => setPricing(r.data)).catch(()=>{});
    gethinhAPI.getPublicStats().then(r => setStats(r.data)).catch(()=>{});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground premium-page">
      <NavBar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 pt-16 pb-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center hero-stage">
            <div>
              <div className="premium-badge inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs text-emerald-200 mb-5">
                <BadgePercent className="w-3 h-3" />
                Abonelik yok · Sadece kullandığın kadar öde
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5">
                <span className="bg-gradient-to-r from-fuchsia-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                  Premium tasarım kaynakları
                </span>
                <br />
                <span className="text-white">8 TL'den başlayan fiyatlarla.</span>
              </h1>

              <p className="text-base sm:text-lg text-muted-foreground mb-7">
                Shutterstock, Envato, Freepik ve 20+ premium platformdan vektör, fotoğraf, ikon dosyalarını
                <strong className="text-foreground"> link yapıştırarak </strong>indir. Ne aylık abonelik ne aşırı pahalı kredi paketleri.
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/register" data-testid="hero-register-btn">
                  <Button size="lg" className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600">
                    Ücretsiz Başla — Hemen Dene
                  </Button>
                </Link>
                <a href="#fiyatlar" data-testid="hero-pricing-btn">
                  <Button size="lg" variant="outline" className="border-white/10">
                    Fiyatları İncele
                  </Button>
                </a>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><X className="w-3.5 h-3.5 text-rose-400" /> Aylık abonelik yok</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Tek tıkla indirme</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Türk Lirası ödeme</span>
                <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-amber-400" /> Saniyeler içinde indir</span>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl">
                <StatPill label="Üye" value={stats?.total_users ?? 0} />
                <StatPill label="İndirme" value={stats?.total_downloads ?? 0} />
                <StatPill label="Aktif Hizmet" value={stats?.active_services ?? 20} />
              </div>
            </div>

            {/* Image collage */}
            <div className="hero-collage-3d">
              <div className="hero-collage-glow" />
              <div className="hero-collage-grid">
                {SAMPLE_IMAGES.map((src, i) => (
                  <div key={i} className={`hero-tile hero-tile-${i}`}>
                    <div className="hero-tile-glow" />
                    <img src={src} alt={`sample-${i}`} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison strip */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="aurora-card rounded-3xl p-6 grid sm:grid-cols-3 gap-6 text-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Shutterstock aylık</p>
            <p className="text-2xl font-bold text-rose-400 line-through opacity-60">~$49 / ay</p>
            <p className="text-xs text-muted-foreground mt-1">aylık 10 indirme limiti</p>
          </div>
          <div className="sm:border-x border-white/10">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Envato Elements</p>
            <p className="text-2xl font-bold text-rose-400 line-through opacity-60">~$16.50 / ay</p>
            <p className="text-xs text-muted-foreground mt-1">aylık taahhüt</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-300 mb-1">CihanStock</p>
            <p className="text-3xl font-extrabold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">8 TL'den</p>
            <p className="text-xs text-emerald-300 mt-1">indirme başına · taahhüt yok</p>
          </div>
        </div>
      </section>



      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-[0.35em] text-fuchsia-300 mb-3">Grafikerler için hızlı iş akışı</p>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Linkten indir, ölçülendir, müşteriye sun.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <ProcessCard step="01" title="Linki yapıştır" desc="Shutterstock, Freepik, Envato veya diğer platform linkini tek alana bırak." />
          <ProcessCard step="02" title="Fiyatı gör" desc="TL fiyatı, mevcut bakiyeni ve sağlayıcı bilgisini anında kontrol et." />
          <ProcessCard step="03" title="İndir ve geçmişte sakla" desc="Dosyanı indir; son indirmelerini panelinden tekrar erişilebilir tut." />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
          <div className="sticky top-24 aurora-card rounded-3xl p-8">
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-300 mb-3">Aktif hizmetler</p>
            <h2 className="text-3xl font-bold mb-4">Tek panelden kreatif operasyon.</h2>
            <p className="text-muted-foreground mb-6">CihanStock; indirme, bakiye, ölçü numunesi, destek ve admin yönetimini tek sistemde toplar.</p>
            <Link to="/register"><Button className="bg-gradient-to-r from-fuchsia-500 to-indigo-500">Hemen Başla</Button></Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <FeatureCard Icon={Download} title="Premium İndirme" desc="Gethinh API altyapısı ile premium kaynakları linkten indir." />
            <FeatureCard Icon={CreditCard} title="Shopier Paketleri" desc="10 TL’den 200 TL’ye esnek bakiye paketleri ile ödeme al." />
            <FeatureCard Icon={Shield} title="E-posta Onayı" desc="Onaylı üyelik, güvenli oturum ve admin kontrolleri." />
            <FeatureCard Icon={Sparkles} title="Ölçü Numunesi" desc="60×90 cm gibi ölçüleri net okunan şık görsel çıktıya dönüştür." />
          </div>
        </div>
      </section>

      {/* Pricing showcase */}
      <section id="fiyatlar" className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-2">Premium kaynaklar, abonesiz fiyatlarla</h2>
          <p className="text-muted-foreground">Dünyanın en büyük tasarım marketlerine indirme başına TL ödeyerek erişin. Aylık taahhüt yok.</p>
        </div>

        {pricing && (() => {
          const entries = Object.entries(pricing.pricing_tl).filter(([k]) => k !== 'default' && k !== 'adobe_stock');
          const popular = POPULAR_ORDER
            .map(k => entries.find(([key]) => key === k))
            .filter(Boolean);
          const others = entries
            .filter(([k]) => !POPULAR_ORDER.includes(k))
            .sort(([, a], [, b]) => a - b);
          return (
            <>
              <p className="text-xs uppercase tracking-widest text-fuchsia-300 mb-3 font-semibold">⭐ Öne Çıkan Markalar</p>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="public-pricing">
                {popular.map(([k, v]) => {
                  const meta = PROVIDER_LOGOS[k] || { name: k, color: 'from-white/5 to-white/0', text: 'text-foreground' };
                  return (
                    <div key={k} className={`premium-pricing-card rounded-3xl p-6 bg-gradient-to-br ${meta.color} border border-white/15 shadow-xl`} data-testid={`public-pricing-${k}`}>
                      <p className={`text-[10px] uppercase tracking-widest mb-1 ${meta.text} font-bold`}>Premium Marka</p>
                      <p className="text-xl font-bold mb-3">{meta.name}</p>
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-extrabold tabular-nums">{v}</span>
                        <span className="text-sm text-muted-foreground">TL/indirme</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {others.length > 0 && (
                <>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Diğer Markalar</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {others.map(([k, v]) => {
                      const meta = PROVIDER_LOGOS[k] || { name: k.charAt(0).toUpperCase() + k.slice(1), color: 'from-white/5 to-white/0', text: 'text-foreground' };
                      return (
                        <div key={k} className={`premium-mini-card rounded-2xl p-4 bg-gradient-to-br ${meta.color} border border-white/10`} data-testid={`public-pricing-${k}`}>
                          <p className="text-sm font-semibold mb-2">{meta.name}</p>
                          <div className="flex items-baseline gap-1">
                            <span className="text-2xl font-bold tabular-nums">{v}</span>
                            <span className="text-xs text-muted-foreground">TL</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          );
        })()}
        <p className="text-center text-xs text-muted-foreground mt-6">Listede olmayan markalar için varsayılan {pricing?.pricing_tl?.default ?? 15} TL geçerlidir.</p>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid md:grid-cols-3 gap-5">
          <FeatureCard Icon={Download} title="Tek Link İndirme" desc="Freepik, Shutterstock, Envato linkini yapıştır. Saniyeler içinde dosyaya ulaş." />
          <FeatureCard Icon={CreditCard} title="TL Bakiye Sistemi" desc="Havale/EFT ile bakiye yükle. Aylık abonelik, otomatik kesinti ya da gizli ücret yok." />
          <FeatureCard Icon={Shield} title="Destek & Geçmiş" desc="Canlı destek talebi oluştur, son 1 ay indirdiğin görselleri geçmişinden tekrar indir." />
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">Birkaç saniye içinde başla.</h2>
        <p className="text-muted-foreground mb-6">Kayıt ücretsiz. İlk indirmen için sadece kullanmak istediğin kadar bakiye yükle.</p>
        <Link to="/register" data-testid="cta-register-btn">
          <Button size="lg" className="bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600">
            <Sparkles className="w-4 h-4 mr-2" /> Hemen Üye Ol
          </Button>
        </Link>
      </section>

      {/* Contact Form */}
      <ContactSection />

      <footer className="border-t border-white/5 py-10 text-center text-xs text-muted-foreground">
        <div className="flex justify-center flex-wrap gap-4 mb-4">
          <Link to="/kvkk" className="hover:text-fuchsia-300 transition-colors">KVKK</Link>
          <Link to="/aydinlatma-metni" className="hover:text-fuchsia-300 transition-colors">Aydinlatma Metni</Link>
          <Link to="/kullanim-sartlari" className="hover:text-fuchsia-300 transition-colors">Kullanim Sartlari</Link>
        </div>
        <p>&copy; {new Date().getFullYear()} CihanStock &mdash; Tum haklari saklidir.</p>
      </footer>
    </div>
  );
}


function ProcessCard({ step, title, desc }) {
  return (
    <div className="aurora-card rounded-3xl p-7 min-h-[210px]">
      <div className="text-5xl font-black text-white/10 mb-4">{step}</div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-6">{desc}</p>
    </div>
  );
}

function FeatureCard({ Icon, title, desc }) {
  return (
    <div className="aurora-card rounded-3xl p-6 premium-feature-card">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500/20 to-indigo-500/20 border border-fuchsia-500/20 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-fuchsia-400" />
      </div>
      <h3 className="text-lg font-semibold mb-1.5">{title}</h3>
      <p className="text-sm text-muted-foreground">{desc}</p>
    </div>
  );
}
function StatPill({ label, value }) {
  return (
    <div className="stat-glass rounded-2xl p-4 text-center">
      <p className="text-2xl font-extrabold text-white tabular-nums">{Number(value).toLocaleString('tr-TR')}</p>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function ContactSection() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error('Ad, e-posta ve mesaj alanlari zorunludur.');
      return;
    }
    setSending(true);
    try {
      await contactAPI.submit(name, email, subject, message);
      toast.success('Mesajiniz basariyla gonderildi!');
      setSent(true);
      setName(''); setEmail(''); setSubject(''); setMessage('');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="iletisim" className="max-w-3xl mx-auto px-4 py-16">
      <div className="aurora-card rounded-3xl p-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Bize Ulasin</h2>
          <p className="text-muted-foreground">Soru, oneri veya sikayet icin formu doldurun. En kisa surede donus yapacagiz.</p>
        </div>

        {sent ? (
          <div className="text-center py-8">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-lg font-semibold text-emerald-400">Mesajiniz alindi!</p>
            <p className="text-sm text-muted-foreground mt-2">En kisa surede size donus yapacagiz.</p>
            <Button onClick={() => setSent(false)} variant="outline" className="mt-4 border-fuchsia-500/30 text-fuchsia-300">
              Yeni Mesaj Gonder
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Adiniz *</label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Ad Soyad" className="bg-white/5 border-white/10" required />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">E-posta *</label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ornek@mail.com" className="bg-white/5 border-white/10" required />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Konu</label>
              <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Konu basligi (opsiyonel)" className="bg-white/5 border-white/10" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Mesajiniz *</label>
              <Textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Mesajinizi buraya yazin..." className="bg-white/5 border-white/10 min-h-[120px]" required />
            </div>
            <Button type="submit" disabled={sending} className="w-full bg-gradient-to-r from-fuchsia-500 to-indigo-500 hover:from-fuchsia-600 hover:to-indigo-600">
              {sending ? 'Gonderiliyor...' : <><Send className="w-4 h-4 mr-2" /> Mesaj Gonder</>}
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}

